import { createOrdersFromQuotes as upstreamCreateOrdersFromQuotes } from 'customer-flows/gql/mutations/create-orders-from-quotes'
import type { CreateOrdersFromQuotesInput, OrderPaymentInput, PaymentMode } from 'create-orders-from-quotes'
import stripeProcessorManager from 'customer-flows/utils/stripe-processor-manager'
import { StripePaymentErrors } from '@spinnaker/iliad/dist/utils/payments/StripePaymentProcessor'
import uid from 'customer-flows/utils/uid'
import Logger from 'customer-flows/utils/logger'
import config from 'customer-flows/config/environment'
import isIosBrowser from 'customer-flows/utils/is-ios-browser'
import { errorMessageOf } from 'customer-flows/utils/error-message'
import { classifyScaFailure } from 'customer-flows/utils/classify-sca-failure'
import { isRedirectPaymentMethod, KLARNA_PAYMENT_METHOD_TYPE } from 'customer-flows/utils/stripe-payment-method-types'

const KLARNA_CAPTURE_FAILURE_ERROR_CODES: Record<AsyncPaymentCaptureReason, CreateOrderFromQuoteErrorCode> = {
  sold_out: 'KLARNA_SOLD_OUT_REFUNDED',
  refund_in_flight: 'KLARNA_REFUND_IN_FLIGHT',
  not_completed: 'KLARNA_CANCELLED'
}

function isAsyncPaymentCaptureReason(value: string): value is AsyncPaymentCaptureReason {
  return value in KLARNA_CAPTURE_FAILURE_ERROR_CODES
}

/**
 * Error codes that require refreshing the payment idempotency key.
 * These are Stripe-specific errors where Stripe has cached the response.
 * Using the same idempotency key would return the same cached error.
 */
const STRIPE_CACHED_ORDER_ERROR_CODES: readonly CreateOrderFromQuoteErrorCode[] = [
  'PAYMENT_DECLINED',
  'KLARNA_DECLINED',
  'KLARNA_CANCELLED',
  'KLARNA_SOLD_OUT_REFUNDED',
  'KLARNA_REFUND_IN_FLIGHT'
]

/**
 * Error codes indicating the previously fetched availability is now stale
 * (e.g. tickets sold out between selection and checkout).
 */
const ERRORS_REQUIRING_AVAILABILITY_REFRESH = ['NO_LONGER_AVAILABLE', 'SELECTED_RESOURCE_AVAILABLE'] as const

export default function payment({ dispatch, stores, callerContext, services }: CommandArgs) {
  const accountStore = stores['account']
  const customerStore = stores['customer']
  const paymentStore = stores['payment']
  const quoteStore = stores['quote']
  const eventTracker: EventTracker = services['event-tracker']
  const frameManager: FrameManager = services['frame-manager']

  // one outcome per payment attempt — an SCA retry continues the attempt that set this
  let outcomeTracked = false

  function trackPaymentOutcome(
    kind: PaymentSubmissionKind,
    { reason, error }: { reason?: PaymentTrackingErrorReason; error?: unknown } = {}
  ) {
    if (outcomeTracked) return

    outcomeTracked = true
    eventTracker.track('ngw_payment_submission', {
      kind,
      reason: reason ?? null,
      error_code: paymentStore.paymentErrorCode,
      error_message: errorMessageOf(error)
    })
  }

  /**
   * Handles 3D Secure Authentication (SCA) flow when required by Stripe
   * @param clientSecret - The client secret from the mutation response
   * @param mutationInput - The original mutation input to retry after SCA
   * @returns Promise that resolves when SCA is complete and payment is retried
   */
  async function handleSca(clientSecret: string, mutationInput: CreateOrdersFromQuotesInput): Promise<void> {
    try {
      await authenticateSca(clientSecret)
    } catch (error) {
      throw failSca(error)
    }

    return await createOrdersFromQuotes(mutationInput, true)
  }

  async function authenticateSca(clientSecret: string): Promise<void> {
    const stripeData = accountStore.stripeData
    if (!stripeData?.publishableKey || !stripeData?.type) {
      throw new Error('Stripe configuration not available for SCA')
    }

    const stripeProcessor = await stripeProcessorManager.getProcessor({
      publishableKey: stripeData.publishableKey,
      type: stripeData.type,
      accountId: stripeData.accountId
    })

    if (!stripeProcessor) {
      throw new Error('Stripe Payment Element processor not available for SCA')
    }

    const { stripeCardActionResult } = await stripeProcessor.handlePaymentResponse({
      requiresAction: { clientSecret }
    })

    if (!stripeCardActionResult?.paymentIntent) {
      throw new Error(StripePaymentErrors.SCA_DENIED)
    }
  }

  /**
   * Resets payment state after a failed SCA attempt so another payment method can be tried.
   * Returns the error to throw: SCA_DENIED when the user failed the challenge, SCA_ERROR otherwise.
   */
  function failSca(error: unknown): Error {
    const failure = classifyScaFailure(error)

    Logger[failure.log.level](failure.log.message, failure.log.data)
    invalidatePaymentMethod()
    failPayment('PAYMENT_DECLINED')
    trackPaymentOutcome(failure.kind, { reason: failure.trackingReason, error: failure.detail })

    return failure.error
  }

  async function createOrdersFromQuotes(mutationInput: CreateOrdersFromQuotesInput, isScaRetry = false): Promise<void> {
    // Only reset error state on the initial call, not on SCA retry
    if (!isScaRetry) {
      setPaymentFormLoading(true)
      setPaymentErrorCode(null)
      outcomeTracked = false
      eventTracker.track('Start Submission')
    }

    try {
      setOrderCreationLoading(true)
      const res = await upstreamCreateOrdersFromQuotes(callerContext, mutationInput)

      const errors = res?.createOrdersFromQuotes?.errors
      const firstError = errors?.[0]
      if (firstError) {
        const errorCode = firstError.code as CreateOrderFromQuoteErrorCode
        failPayment(errorCode)
        Logger.warn('Payment: Order creation returned an error', { code: errorCode, detail: firstError.detail })

        // The availability the user saw is no longer trustworthy — it just caused a failed order.
        // Hard-invalidate it so the tickets step can't reuse it: the data stops being served and
        // refetches as a blocking first-load on re-entry. Not awaited — the engine owns the refetch
        // lifecycle and the throw below must still surface promptly.
        if (ERRORS_REQUIRING_AVAILABILITY_REFRESH.includes(errorCode as any)) {
          dispatch('availability:invalidateAvailability')
        }

        throw new Error(firstError.detail || firstError.code)
      }

      // Handle 3D Secure Authentication (SCA) if required
      const requiresAction = res?.createOrdersFromQuotes?.requiresAction
      if (requiresAction?.clientSecret) {
        Logger.warn('Payment: 3D Secure authentication required')
        // SCA retry will use the SAME idempotency key (continuation of same payment)
        return await handleSca(requiresAction.clientSecret, mutationInput)
      }

      // The card issuer discount is applied optimistically when the customer checks the box,
      // but only verified against the actual card at payment authorization time
      if (requiresAction?.cardIssuerDiscountAmount) {
        Logger.warn('Payment: Card issuer discount ineligible for this card')
        await dispatch('quote:disableCardIssuerDiscount')
        setPaymentErrorCode('CARD_ISSUER_DISCOUNT_INELIGIBLE')
        throw new Error('CARD_ISSUER_DISCOUNT_INELIGIBLE')
      }

      const order = res?.createOrdersFromQuotes?.orders?.[0]

      if (!order) throw new Error('No order created')

      Logger.info('Payment: Order created successfully', { orderId: order.id })
      dispatch('order:setOrder', order)
      paymentStore.hasSuccessfulPayment = true

      trackPaymentOutcome('success')

      eventTracker.track('Complete', {
        tippingAdded: quoteStore.tippingAdded,
        peek_protect_added: quoteStore.hasPeekProtect
      })
    } catch (error) {
      Logger.error('Failed to create orders from quotes', { error })
      trackPaymentOutcome(paymentStore.paymentErrorCode === 'PAYMENT_DECLINED' ? 'declined' : 'error', {
        reason: paymentStore.paymentErrorCode ? 'order_rejected' : 'exception',
        error
      })
      throw error
    } finally {
      setOrderCreationLoading(false)
      setPaymentFormLoading(false)
    }
  }

  async function authorizeKlarna(clientSecret: string): Promise<void> {
    const result = await dispatch('payment/klarna:authorize', clientSecret)

    if (result.status === 'authorized') return

    if (result.status === 'declined') {
      failPayment('KLARNA_DECLINED')
      throw new Error(result.message || 'DECLINED')
    }

    if (result.status === 'card-issuer-discount-ineligible') {
      await dispatch('quote:disableCardIssuerDiscount')
      setPaymentErrorCode('CARD_ISSUER_DISCOUNT_INELIGIBLE')
      throw new Error('CARD_ISSUER_DISCOUNT_INELIGIBLE')
    }

    failPayment(result.errorCode)
    if (ERRORS_REQUIRING_AVAILABILITY_REFRESH.includes(result.errorCode as any)) {
      dispatch('availability:invalidateAvailability')
    }
    throw new Error(result.message)
  }

  function preOpenIosTab(): Window | null {
    if (!isRedirectPaymentMethod(KLARNA_PAYMENT_METHOD_TYPE) || !isIosBrowser()) return null

    return window.open('', '_blank')
  }

  function closeKlarnaTab(): void {
    const tab = paymentStore.preOpenedKlarnaTab
    if (tab && !tab.closed) {
      tab.close()
    }

    paymentStore.preOpenedKlarnaTab = null
  }

  function teardownKlarnaAttempt(): void {
    closeKlarnaTab()
    stopListeningForAsyncPaymentCapture()
  }

  async function redirectToKlarnaAndAwaitCapture(clientSecret: string): Promise<string> {
    const capturePromise = listenForAsyncPaymentCapture(clientSecret)
    const { paymentIntentId, authorizationUrl } = await dispatch('payment/klarna:confirmAndGetAuthorizationUrl')

    if (paymentStore.preOpenedKlarnaTab && !paymentStore.preOpenedKlarnaTab.closed) {
      paymentStore.preOpenedKlarnaTab.location.replace(authorizationUrl)
    } else {
      paymentStore.preOpenedKlarnaTab = frameManager.redirectToNewTab(authorizationUrl)
    }

    const tabClosedWatch = dispatch('payment/klarna:watchForTabClosed', clientSecret)
    try {
      await Promise.race([capturePromise, tabClosedWatch.promise])
    } finally {
      tabClosedWatch.cancel()
    }

    return paymentIntentId
  }

  async function payWithKlarna(): Promise<void> {
    setPaymentFormLoading(true)
    setPaymentErrorCode(null)
    outcomeTracked = false
    paymentStore.preOpenedKlarnaTab = preOpenIosTab()

    try {
      const clientSecret = paymentStore.stripeClientSecret
      if (!clientSecret) {
        throw new Error('No Stripe client secret available for Klarna authorization')
      }

      await authorizeKlarna(clientSecret)

      const paymentIntentId = await redirectToKlarnaAndAwaitCapture(clientSecret)

      const orderInput = buildOrderInput({
        paymentMode: 'ALREADY_CAPTURED',
        payment: alreadyCapturedPayment(paymentIntentId)
      })
      await createOrdersFromQuotes(orderInput)
    } catch (error) {
      Logger.error('Failed to pay with Klarna', { error })
      teardownKlarnaAttempt()

      if (error instanceof Error && isAsyncPaymentCaptureReason(error.message)) {
        failPayment(KLARNA_CAPTURE_FAILURE_ERROR_CODES[error.message])
      }

      trackPaymentOutcome(paymentStore.isDeclined ? 'declined' : 'error', { reason: 'klarna_failed', error })
      throw error
    } finally {
      setPaymentFormLoading(false)
    }
  }

  /**
   * Stored value (gift cards, store credit) already authorized on the quote.
   * Each is a separate payment operation, so each gets its own fresh idempotency key.
   */
  function storedValuePayments(): OrderPaymentInput[] {
    const authorizedPayments: StoredValuePayment[] = quoteStore.authorizedPayments ?? []

    return authorizedPayments.map((payment) => ({
      idempotencyKey: uid(),
      processor: 'STORED_VALUE',
      paymentSourceId: payment.paymentSource?.id,
      amount: {
        amount: parseFloat(payment.authorizedAmount?.amount ?? '0'),
        currency: (payment.authorizedAmount?.currency ?? 'USD') as Currency
      }
    }))
  }

  function owedAmount(): { amount: number; currency: Currency } {
    const priceBreakdown = quoteStore.priceBreakdown
    const owed = quoteStore.authorizedPayments?.length
      ? priceBreakdown?.totalMinusStoredValueAuthorized
      : priceBreakdown?.total

    return { amount: parseFloat(owed?.amount ?? '0'), currency: (owed?.currency ?? 'USD') as Currency }
  }

  /**
   * The card charge for whatever stored value leaves unpaid, or nothing when it covers the order.
   * The card idempotency key is owned by this command and refreshed on Stripe errors.
   * @param paymentMethod - Stripe payment method to charge
   */
  function cardPayment(paymentMethod?: PaymentMethod | null): OrderPaymentInput | undefined {
    const { amount, currency } = owedAmount()

    if (amount <= 0) return undefined

    if (!accountStore.stripeData?.type) {
      throw new Error('Payment processor type is required but not available')
    }

    return {
      idempotencyKey: paymentStore.idempotencyKey,
      processor: accountStore.stripeData.type,
      token: paymentMethod?.id,
      amount: { amount, currency }
    }
  }

  function alreadyCapturedPayment(paymentIntentId: string): OrderPaymentInput | undefined {
    const { amount, currency } = owedAmount()

    if (amount <= 0) return undefined

    if (!accountStore.stripeData?.type) {
      throw new Error('Payment processor type is required but not available')
    }

    return {
      idempotencyKey: paymentStore.idempotencyKey,
      processor: accountStore.stripeData.type,
      token: paymentIntentId,
      amount: { amount, currency }
    }
  }

  /**
   * Builds the createOrdersFromQuotes mutation input for the current quote.
   * @param paymentMethod - Stripe payment method to charge; defaults to the one held in the store
   * @param paymentMode - How the order settles; defaults to a full payment
   * @param payment - Explicit payment entry, overriding the card payment built from paymentMethod
   */
  function buildOrderInput({
    paymentMethod = paymentStore.paymentMethod,
    paymentMode = 'FULL_PAYMENT',
    payment
  }: {
    paymentMethod?: PaymentMethod | null
    paymentMode?: PaymentMode
    payment?: OrderPaymentInput
  } = {}): CreateOrdersFromQuotesInput {
    const orderPayment = payment ?? cardPayment(paymentMethod)

    return {
      input: {
        quoteIds: [quoteStore.id],
        customer: {
          name: customerStore.name,
          email: customerStore.email,
          phone: customerStore.phone,
          country: paymentMethod?.billing_details?.address?.country || 'US'
        },
        paymentMode,
        payments: [...storedValuePayments(), ...(orderPayment ? [orderPayment] : [])]
      }
    }
  }

  function initPaymentForm() {
    paymentStore.stripeFormReady = true
  }

  function updatePaymentFormState(state: any) {
    const newPaymentMethodType = state?.value?.type ?? null

    if (newPaymentMethodType !== paymentStore.selectedPaymentMethodType) {
      setPaymentErrorCode(null)
    }

    paymentStore.paymentFormIsValid = Boolean(state?.complete)
    paymentStore.paymentFormIsEmpty = Boolean(state?.empty)
    paymentStore.selectedPaymentMethodType = newPaymentMethodType

    if (paymentStore.paymentFormIsValid && !paymentStore.paymentFormIsEmpty) {
      eventTracker.track('add_payment_info')
    }
  }

  function setPaymentMethod(paymentMethod: PaymentMethod | null) {
    paymentStore.paymentMethod = paymentMethod
  }

  function setStripeClientSecret(clientSecret: string | null) {
    paymentStore.stripeClientSecret = clientSecret
  }

  function setPaymentFormLoading(isLoading: boolean) {
    paymentStore.paymentFormIsLoading = isLoading
  }

  function setOrderCreationLoading(isLoading: boolean) {
    paymentStore.isCreatingOrder = isLoading
  }

  function setPaymentErrorCode(errorCode: CreateOrderFromQuoteErrorCode | null) {
    paymentStore.paymentErrorCode = errorCode
  }

  function failPayment(errorCode: CreateOrderFromQuoteErrorCode) {
    setPaymentErrorCode(errorCode)

    if (STRIPE_CACHED_ORDER_ERROR_CODES.includes(errorCode)) {
      refreshIdempotencyKey()
    }
  }

  function invalidatePaymentMethod() {
    paymentStore.paymentMethod = null
    paymentStore.paymentMethodRefreshCount += 1
  }

  function refreshIdempotencyKey() {
    paymentStore.idempotencyKey = uid()
  }

  function setAvailableExpressCheckoutMethods(methods: string[]) {
    paymentStore.availableExpressCheckoutMethods = methods
  }

  function stopListeningForAsyncPaymentCapture() {
    paymentStore.klarnaCaptureConnection?.close()
    paymentStore.klarnaCaptureConnection = null
  }

  function openAsyncCaptureConnection(clientSecret: string): EventSource {
    // Same-origin via fire-pit, not paymentEventsServer directly — noreaga's CORS can't serve
    // that credentialed cross-origin (see main's payment-source.js for the same pattern).
    const url = `${config.apiSubmitServer}/payment_events?client_secret=${clientSecret}`
    const eventSource = new EventSource(url, { withCredentials: true })
    paymentStore.klarnaCaptureConnection = eventSource

    return eventSource
  }

  function closeAsyncCaptureConnection(eventSource: EventSource) {
    eventSource.close()
    if (paymentStore.klarnaCaptureConnection === eventSource) paymentStore.klarnaCaptureConnection = null
  }

  function asyncCaptureFailureReason(detail?: AsyncCaptureFailureDetail): AsyncPaymentCaptureReason {
    if (detail === 'NO_AVAILABILITY') return 'sold_out'
    if (detail === 'ORDER_CREATION_ERROR' || detail === 'UNHANDLED_EXCEPTION') return 'refund_in_flight'
    return 'not_completed'
  }

  /**
   * Interprets one SSE payload from the payment-events channel. `connected` is a handshake ack,
   * not an outcome, so the caller should keep waiting; every other kind ends the connection.
   */
  function parseAsyncCaptureMessage(rawData: string): AsyncCaptureMessage {
    const response = JSON.parse(rawData) as AsyncCaptureResponse

    if (response.connected) return { kind: 'connected' }
    if (response.reconnect) return { kind: 'reconnect' }

    if (!response.success) {
      return { kind: 'failure', reason: asyncCaptureFailureReason(response.detail) }
    }

    return { kind: 'success' }
  }

  /**
   * Waits for an out-of-band payment (e.g. Klarna) to be captured, via a server-sent-events
   * channel keyed on the Stripe client secret. Resolves once noreaga reports success; rejects
   * with the failure reason otherwise. Starting a new attempt closes any listener already open.
   */
  function listenForAsyncPaymentCapture(clientSecret: string): Promise<void> {
    stopListeningForAsyncPaymentCapture()

    return new Promise((resolve, reject) => {
      const eventSource = openAsyncCaptureConnection(clientSecret)

      eventSource.addEventListener('message', (event: MessageEvent) => {
        const message = parseAsyncCaptureMessage(event.data)

        if (message.kind === 'connected') return

        closeAsyncCaptureConnection(eventSource)

        if (message.kind === 'reconnect') {
          listenForAsyncPaymentCapture(clientSecret).then(resolve).catch(reject)
          return
        }

        if (message.kind === 'failure') {
          reject(new Error(message.reason))
          return
        }

        resolve()
      })

      eventSource.addEventListener('error', () => {
        if (eventSource.readyState === EventSource.CLOSED) {
          closeAsyncCaptureConnection(eventSource)
          // The connection dropped before noreaga reported an outcome.
          reject(new Error('not_completed' satisfies AsyncPaymentCaptureReason))
        }
      })
    })
  }

  return {
    buildOrderInput,
    createOrdersFromQuotes,
    payWithKlarna,
    initPaymentForm,
    updatePaymentFormState,
    setPaymentMethod,
    setStripeClientSecret,
    setPaymentFormLoading,
    setOrderCreationLoading,
    setPaymentErrorCode,
    refreshIdempotencyKey,
    setAvailableExpressCheckoutMethods,
    listenForAsyncPaymentCapture,
    stopListeningForAsyncPaymentCapture,
    teardownKlarnaAttempt
  }
}

declare global {
  export type PaymentCommands = ReturnType<typeof payment>

  // Add to global scope
  interface CommandRegistry {
    payment: PaymentCommands
  }
}
